import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Link as AliasIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';

// Types
interface Fund {
  BackstopID: number;
  FundName: string;
  Strategy?: string;
  Currency?: string;
}

interface FundAlias {
  AliasID: number;
  BackstopID: number;
  AliasName: string;
  FundName?: string;
  CreatedDate?: string;
  ModifiedDate?: string;
  UserName?: string;
}

const FundAliasManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [aliases, setAliases] = useState<FundAlias[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    BackstopID: '',
    AliasName: ''
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    BackstopID: '',
    AliasName: ''
  });
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aliasToDelete, setAliasToDelete] = useState<FundAlias | null>(null);

  // Load data on component mount
  useEffect(() => {
    loadFunds();
    loadAliases();
  }, []);

  // Load funds data
  const loadFunds = async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/funds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load funds');
      }
      
      const data = await response.json();
      setFunds(Array.isArray(data) ? data : []);
      
    } catch (err) {
      console.error('Error loading funds:', err);
      // STRICT: No sample data - only real database data
      setFunds([]);
    }
  };

  // Load aliases data
  const loadAliases = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-aliases`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load fund aliases');
      }
      
      const data = await response.json();
      setAliases(Array.isArray(data) ? data : []);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fund aliases');
      console.error('Error loading fund aliases:', err);
      
      // STRICT: No sample data - only real database data
      setAliases([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle form changes
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      // Validate alias name uniqueness
      const existingAlias = aliases.find(alias => 
        alias.AliasName.toLowerCase() === formData.AliasName.toLowerCase() &&
        alias.AliasID !== editingId
      );
      
      if (existingAlias) {
        throw new Error('Alias name already exists. Please choose a different name.');
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-aliases/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-aliases`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          BackstopID: parseInt(formData.BackstopID),
          AliasName: formData.AliasName.trim()
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${editingId ? 'update' : 'create'} fund alias`);
      }
      
      setSuccess(`Fund alias ${editingId ? 'updated' : 'created'} successfully`);
      handleClear();
      loadAliases(); // Reload the aliases list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (alias: FundAlias) => {
    setFormData({
      BackstopID: alias.BackstopID.toString(),
      AliasName: alias.AliasName
    });
    setEditingId(alias.AliasID);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!aliasToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/fund-aliases/${aliasToDelete.AliasID}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete fund alias');
      }
      
      setSuccess('Fund alias deleted successfully');
      setDeleteDialogOpen(false);
      setAliasToDelete(null);
      loadAliases(); // Reload the aliases list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({ ...formData });
    setPage(0); // Reset pagination when filtering
  };

  // Handle clear form
  const handleClear = () => {
    setFormData({
      BackstopID: '',
      AliasName: ''
    });
    setAppliedFilters({
      BackstopID: '',
      AliasName: ''
    });
    setEditingId(null);
  };

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.BackstopID || formData.AliasName;

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.BackstopID || appliedFilters.AliasName;

  // Filter aliases
  const filteredAliases = aliases.filter(alias => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.BackstopID || alias.BackstopID.toString() === appliedFilters.BackstopID) &&
      (!appliedFilters.AliasName || alias.AliasName.toLowerCase().includes(appliedFilters.AliasName.toLowerCase()))
    );
    
    return matchesFormFilters;
  });

  // Get fund name by BackstopID
  const getFundName = (backstopId: number) => {
    return funds.find(fund => fund.BackstopID === backstopId)?.FundName || `Fund ${backstopId}`;
  };

  // Paginated aliases
  const paginatedAliases = filteredAliases.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Fund Alias Management
      </Typography>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2 }} data-form-section>
            <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AliasIcon />
              {editingId ? 'Edit Fund Alias' : hasFilterCriteria ? 'Add Fund Alias / Search Filters' : 'Add New Fund Alias'}
            </Typography>
            {hasFilterCriteria && !editingId && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  Form fields are being used to filter the aliases list. Click "Search" to apply filters or "Clear" to reset.
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel>Fund Name *</InputLabel>
                <Select
                  value={formData.BackstopID}
                  onChange={(e) => handleInputChange('BackstopID', e.target.value)}
                  label="Fund Name *"
                >
                  {funds.map(fund => (
                    <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                      {fund.FundName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Alias Name *"
                value={formData.AliasName}
                onChange={(e) => handleInputChange('AliasName', e.target.value)}
                fullWidth
                required
                size="small"
                helperText="Enter an alternative name for the fund"
              />

              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSubmit}
                  disabled={loading || !formData.BackstopID || !formData.AliasName.trim()}
                  sx={{ flex: hasFilterCriteria && !editingId ? 1 : 2 }}
                >
                  {loading ? 'Processing...' : (editingId ? 'Update' : 'Create')}
                </Button>
                {hasFilterCriteria && !editingId && (
                  <Button
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    Search
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Instructions */}
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Fund Alias Instructions
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Fund aliases allow you to create alternative names for funds, making them easier to identify and reference.
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <li>
                <Typography variant="body2">
                  Select the fund you want to create an alias for
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Enter a unique alias name (duplicates not allowed)
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Use aliases for reporting, data import, or easier fund identification
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Double-click any alias in the table to edit it
                </Typography>
              </li>
            </Box>
          </Paper>
        </Grid>

        {/* Table Section */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box>
                <Typography variant="h6">
                  Fund Aliases ({filteredAliases.length} of {aliases.length})
                </Typography>
                {hasAppliedFilters && (
                  <Typography variant="body2" color="text.secondary">
                    Filtered by form criteria
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={loadAliases} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Alias Name</TableCell>
                    <TableCell>Fund Name</TableCell>
                    <TableCell>Backstop ID</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAliases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No fund aliases found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAliases.map((alias) => (
                      <TableRow 
                        key={alias.AliasID}
                        onDoubleClick={() => handleEdit(alias)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                      >
                        <TableCell>
                          <Chip 
                            label={alias.AliasName} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{getFundName(alias.BackstopID)}</TableCell>
                        <TableCell>{alias.BackstopID}</TableCell>
                        <TableCell>
                          {alias.CreatedDate ? new Date(alias.CreatedDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{alias.UserName || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit Alias">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(alias)}
                                disabled={loading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Alias">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setAliasToDelete(alias);
                                  setDeleteDialogOpen(true);
                                }}
                                disabled={loading}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredAliases.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete Fund Alias</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the alias "{aliasToDelete?.AliasName}" 
            for fund "{getFundName(aliasToDelete?.BackstopID || 0)}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundAliasManagement;
